import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LogOut, 
  User, 
  Mail, 
  Shield, 
  Calendar,
  Home,
  Layout,
  Globe,
  Server,
  CreditCard,
  Store,
  Search,
  Bell,
  HelpCircle,
  Menu,
  X,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  FileText,
  Clock,
  Send,
  CalendarCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from '../components/ui/sonner';
import Campaigns from '../components/Campaigns';
import WebLeads from '../components/WebLeads';
import Bookings from '../components/Bookings';
import { clearAuth } from '../lib/auth';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Load active section from localStorage on initialization
  const [activeSection, setActiveSection] = useState(() => {
    const savedSection = localStorage.getItem('dashboardActiveSection');
    return savedSection || 'home';
  });
  const [notifications] = useState(2);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      if (isMobile) {
        setSidebarOpen(false);
      } else {
        // Restore sidebar state from localStorage on desktop
        const savedSidebarState = localStorage.getItem('sidebarOpen');
        if (savedSidebarState !== null) {
          setSidebarOpen(savedSidebarState === 'true');
        }
      }
    };

    // Check on mount
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Persist sidebar state to localStorage when it changes (only on desktop)
  useEffect(() => {
    if (window.innerWidth >= 768) {
      localStorage.setItem('sidebarOpen', sidebarOpen.toString());
    }
  }, [sidebarOpen]);

  // Persist active section to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('dashboardActiveSection', activeSection);
  }, [activeSection]);

  useEffect(() => {
    // ProtectedRoute already handles authentication, just load user data
    const userData = localStorage.getItem('user');

    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        // Clear invalid data and redirect
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('isAuthenticated');
        navigate('/dashboard', { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleLogout = () => {
    clearAuth();
    toast.success('Logged out successfully');
    navigate('/dashboard', { replace: true });
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const sidebarItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'web-leads', label: 'Web Leads', icon: FileText },
    { id: 'bookings', label: 'Bookings', icon: CalendarCheck },
    { id: 'campaigns', label: 'Campaigns', icon: Send },
    { id: 'projects', label: 'Projects', icon: Layout },
    { id: 'clients', label: 'Clients', icon: Mail },
    { id: 'domains', label: 'Domains', icon: Globe },
    { id: 'hosting', label: 'Hosting', icon: Server },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'marketplace', label: 'Marketplace', icon: Store },
  ];

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1c1b]">
        <div className="text-[#d9fb06]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#1a1c1b] flex overflow-hidden">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} fixed md:relative md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'} z-40 bg-[#302f2c] border-r border-[#3f4816] transition-all duration-300 flex flex-col h-full`}>
        {/* Logo */}
        <div className="p-6 border-b border-[#3f4816]">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-[#d9fb06] rounded flex items-center justify-center">
                  <span className="text-[#1a1c1b] font-bold text-sm">AD</span>
                </div>
                <span className="text-[#d9fb06] font-bold text-lg">Axios Dev</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-[#888680] hover:text-[#d9fb06] hover:bg-[#3f4816]"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  // Close sidebar on mobile after selection
                  if (window.innerWidth < 768) {
                    setSidebarOpen(false);
                  }
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-[#3f4816] text-[#d9fb06]'
                    : 'text-[#888680] hover:bg-[#3f4816] hover:text-[#d9fb06]'
                }`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-[#302f2c] border-b border-[#3f4816] px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden text-[#888680] hover:text-[#d9fb06] hover:bg-[#3f4816] mr-4"
            >
              <Menu className="h-5 w-5" />
            </Button>
            {/* Search */}
            <div className="flex-1 max-w-2xl mr-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#888680]" />
                <Input
                  type="text"
                  placeholder="Search... e.g. How do I create a project?"
                  className="pl-10 bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06] w-full"
                />
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                className="relative text-[#888680] hover:text-[#d9fb06] hover:bg-[#3f4816]"
              >
                <Bell className="h-5 w-5" />
                {notifications > 0 && (
                  <span className="absolute top-0 right-0 h-4 w-4 bg-[#d9fb06] text-[#1a1c1b] rounded-full text-xs flex items-center justify-center font-bold">
                    {notifications}
                  </span>
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-[#888680] hover:text-[#d9fb06] hover:bg-[#3f4816]"
              >
                <HelpCircle className="h-5 w-5" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center space-x-3 focus:outline-none focus:ring-2 focus:ring-[#d9fb06] focus:ring-offset-2 focus:ring-offset-[#1a1c1b] rounded-lg p-1">
                    <div className="w-10 h-10 rounded-full bg-[#d9fb06] flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                      <span className="text-[#1a1c1b] font-bold text-sm">
                        {getInitials(user.name)}
                      </span>
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-white">{user.name}</p>
                      <p className="text-xs text-[#888680]">{user.role}</p>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-56 bg-[#302f2c] border-[#3f4816] text-white"
                >
                  <DropdownMenuLabel className="text-[#888680] text-xs font-normal px-3 py-2">
                    Signed in as {user.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#3f4816]" />
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-[#3f4816] focus:bg-[#3f4816] text-white"
                    onClick={() => setActiveSection('home')}
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Home
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-[#3f4816] focus:bg-[#3f4816] text-white"
                    onClick={() => {
                      // Scroll to profile section or handle profile view
                      const profileCard = document.querySelector('[data-profile-section]');
                      if (profileCard) {
                        profileCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    <User className="mr-2 h-4 w-4" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#3f4816]" />
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-[#3f4816] focus:bg-[#3f4816] text-red-500 focus:text-red-500"
                    onClick={handleLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-6">
          {activeSection === 'web-leads' ? (
            <WebLeads />
          ) : activeSection === 'bookings' ? (
            <Bookings />
          ) : activeSection === 'campaigns' ? (
            <Campaigns />
          ) : (
            <>
              {/* Important Alert Banner */}
              <div className="mb-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-yellow-500 font-semibold text-sm">Important</p>
                  <p className="text-[#888680] text-sm mt-1">
                    Your profile information needs to be updated. Please verify your email address.
                  </p>
                </div>
              </div>

              {/* Personalized Greeting */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-white">
                  Hi, {user.name.split(' ')[0]}!
                </h1>
                <p className="text-[#888680] mt-1">Welcome back to your dashboard</p>
              </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#888680]">Projects</p>
                  <Layout className="h-5 w-5 text-[#d9fb06]" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">12</p>
                <a href="#" className="text-sm text-[#d9fb06] hover:underline">
                  View Projects
                </a>
              </CardContent>
            </Card>

            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#888680]">Active Clients</p>
                  <Mail className="h-5 w-5 text-[#d9fb06]" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">8</p>
                <a href="#" className="text-sm text-[#d9fb06] hover:underline">
                  View Clients
                </a>
              </CardContent>
            </Card>

            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#888680]">Tasks Completed</p>
                  <TrendingUp className="h-5 w-5 text-[#d9fb06]" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">87%</p>
                <a href="#" className="text-sm text-[#d9fb06] hover:underline">
                  View Progress
                </a>
              </CardContent>
            </Card>

            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-[#888680]">Notifications</p>
                  <Bell className="h-5 w-5 text-[#d9fb06]" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">{notifications}</p>
                <a href="#" className="text-sm text-[#d9fb06] hover:underline">
                  View All
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Promotional Banner */}
          <Card className="mb-6 border-[#3f4816] bg-gradient-to-r from-[#3f4816] to-[#302f2c]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">
                    Get your projects organized with expert help
                  </h3>
                  <p className="text-[#888680] mb-4">
                    We'll help you set up your workflow and manage your projects more efficiently.
                  </p>
                  <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e005] font-semibold">
                    Schedule a Call
                  </Button>
                </div>
                <div className="hidden lg:block ml-8">
                  <div className="w-32 h-32 bg-[#3f4816] rounded-lg flex items-center justify-center">
                    <Layout className="h-16 w-16 text-[#d9fb06] opacity-50" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Projects */}
            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardHeader>
                <CardTitle className="text-[#d9fb06] flex items-center space-x-2">
                  <Layout className="h-5 w-5" />
                  <span>Recent Projects</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-[#1a1c1b] rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#3f4816] rounded flex items-center justify-center">
                        <FileText className="h-5 w-5 text-[#d9fb06]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">E-commerce Platform</p>
                        <p className="text-sm text-[#888680]">In Progress</p>
                      </div>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      Active
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#1a1c1b] rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#3f4816] rounded flex items-center justify-center">
                        <Globe className="h-5 w-5 text-[#d9fb06]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Client Website</p>
                        <p className="text-sm text-[#888680]">Completed</p>
                      </div>
                    </div>
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                      Done
                    </Badge>
                  </div>
                </div>
                <Button variant="ghost" className="w-full mt-4 text-[#d9fb06] hover:bg-[#3f4816]">
                  Manage Projects
                </Button>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="border-[#3f4816] bg-[#302f2c]">
              <CardHeader>
                <CardTitle className="text-[#d9fb06] flex items-center space-x-2">
                  <Clock className="h-5 w-5" />
                  <span>Recent Activity</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-3 bg-[#1a1c1b] rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-white text-sm">Project "E-commerce Platform" updated</p>
                      <p className="text-xs text-[#888680] mt-1">2 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 bg-[#1a1c1b] rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-white text-sm">New client added</p>
                      <p className="text-xs text-[#888680] mt-1">5 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 bg-[#1a1c1b] rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-white text-sm">Task completed</p>
                      <p className="text-xs text-[#888680] mt-1">1 day ago</p>
                    </div>
                  </div>
                </div>
                <Button variant="ghost" className="w-full mt-4 text-[#d9fb06] hover:bg-[#3f4816]">
                  View All Activity
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Profile Section */}
          <Card className="mt-6 border-[#3f4816] bg-[#302f2c]" data-profile-section>
            <CardHeader>
              <CardTitle className="text-[#d9fb06]">Your Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-[#d9fb06]" />
                  <div>
                    <p className="text-sm text-[#888680]">Name</p>
                    <p className="text-white font-medium">{user.name}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-[#d9fb06]" />
                  <div>
                    <p className="text-sm text-[#888680]">Email</p>
                    <p className="text-white font-medium">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-[#d9fb06]" />
                  <div>
                    <p className="text-sm text-[#888680]">Role</p>
                    <p className="text-white font-medium capitalize">{user.role}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-[#d9fb06]" />
                  <div>
                    <p className="text-sm text-[#888680]">Member Since</p>
                    <p className="text-white font-medium">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex space-x-3">
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="border-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
                <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e005]">
                  Edit Profile
                </Button>
              </div>
            </CardContent>
          </Card>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
